import { useId } from 'react'
import { DialogError, Kicker, ModalDialog } from '../tenants/modal-dialog.tsx'

/**
 * The confirmation before a stray flag key is removed from a tenant —
 * docs/ux/PLATFORM-ADMIN.md §14.2 and §19: "Remove an unknown flag key … No (the value is
 * gone) … The key, its current value, and that the tenant stops resolving it on its next
 * request."
 *
 * It is the same shell as the console's four other confirmations (`ModalDialog`: a real modal,
 * focus in and back, Escape refused — twice). **It is a danger confirm, so no dismissible mode
 * was needed** (P06 reserved one for the first dialog that is not): removal cannot be undone,
 * since nothing keeps the value, and a stray Escape must not be the difference between acting
 * and not. Cancel is the way out and is inert while the request is in flight.
 *
 * **A failed removal stays here** with the sentence for its code and the confirm usable again;
 * the key is still in the table behind it. There is no typed field: the key is on screen in the
 * title and the table, and the act is one row's value.
 */
export function RemoveFlagDialog({
  flagKey,
  value,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  readonly flagKey: string
  /** The value the tenant holds for the key, as it was when the dialog opened. */
  readonly value: boolean
  readonly pending: boolean
  /** A failed confirm's sentence, shown inside the dialog. */
  readonly error?: string | undefined
  readonly onCancel: () => void
  readonly onConfirm: () => void
}) {
  const headingId = useId()

  return (
    <ModalDialog
      labelledBy={headingId}
      foot={
        <div className="confirm-controls">
          <button type="button" className="button" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className="button button-danger"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? 'Removing…' : 'Remove the key'}
          </button>
        </div>
      }
    >
      <Kicker lead="Data change" />
      <h2 id={headingId}>
        Remove <code>{flagKey}</code> from this tenant?
      </h2>
      <p>
        It is currently <code>{value ? 'true' : 'false'}</code>. From the tenant’s next request it
        stops resolving at all, as if it had never been set. The value is not kept anywhere, so
        there is no way back to it.
      </p>

      <DialogError>{error}</DialogError>
    </ModalDialog>
  )
}
